import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Check, X, ArrowRight, RotateCcw, Trophy, Volume2, VolumeX } from "lucide-react";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAudio } from "../lib/useAudio";
import { log } from "../lib/logger";

function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

export default function Review() {
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [done, setDone] = useState(false);
  const [xpGained, setXpGained] = useState(0);
  const nav = useNavigate();
  const { pronounce } = useAudio();
  const [autoplay, setAutoplay] = useState(() => {
    try { return localStorage.getItem("ll_autoplay") !== "0"; } catch { return true; }
  });
  const toggleAutoplay = () => {
    setAutoplay((v) => {
      const nv = !v;
      try { localStorage.setItem("ll_autoplay", nv ? "1" : "0"); } catch {}
      return nv;
    });
  };

  useEffect(() => {
    api.get("/reviews/due").then(({ data }) => {
      setCards(shuffle(data.cards));
      if (!data.cards.length) setDone(true);
    });
  }, []);

  const card = cards[idx];

  // Auto-play the target-language front when a new card appears.
  useEffect(() => {
    if (!card || !autoplay || done) return undefined;
    const t = setTimeout(() => {
      pronounce(card).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, autoplay, done]);

  const grade = async (quality) => {
    try {
      const { data } = await api.post("/reviews", {
        card_id: card.id,
        course_id: card.course_id,
        quality,
        mode: "flashcard",
      });
      setXpGained((v) => v + (data.xp_gain || 0));
    } catch (err) {
      log.error("Review grading failed", err);
    }
    if (idx + 1 >= cards.length) {
      setDone(true);
    } else {
      setIdx((i) => i + 1);
      setShowBack(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grain-bg" data-testid="review-complete">
        <Nav />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="brutal-card p-12 bg-white">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-brand-mint border-2 border-brand-ink shadow-brutal flex items-center justify-center mb-6">
              <Trophy className="w-10 h-10 text-brand-navy" strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">
              {cards.length === 0 ? "Nothing due! 🎉" : "Review done!"}
            </h1>
            <p className="text-brand-navy/70 mt-2 text-lg">
              {cards.length === 0 ? "Come back tomorrow, or start a new deck." : `You earned +${xpGained} XP. Great job!`}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/dashboard" className="brutal-btn-white">Home</Link>
              <Link to="/courses" className="brutal-btn-pink">Browse courses <ArrowRight className="w-5 h-5" strokeWidth={3} /></Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen grain-bg">
        <Nav />
        <div className="max-w-4xl mx-auto p-8">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg bg-brand-peach" data-testid="review-page">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="brutal-badge bg-white mb-2"><RotateCcw className="w-3.5 h-3.5" strokeWidth={3} /> Spaced Review</div>
            <div className="font-bold text-brand-navy/70">Card {idx + 1} of {cards.length}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAutoplay}
              data-testid="review-autoplay-toggle"
              className={`brutal-badge ${autoplay ? "bg-brand-mint" : "bg-white"} text-xs`}
              title={autoplay ? "Auto-play is on. Click to mute." : "Auto-play is off. Click to unmute."}
              type="button"
            >
              {autoplay
                ? (<><Volume2 className="w-3.5 h-3.5" strokeWidth={3} /> Auto sound</>)
                : (<><VolumeX className="w-3.5 h-3.5" strokeWidth={3} /> Muted</>)}
            </button>
            <div className="flex items-center gap-2 brutal-badge bg-brand-yellow">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={3} /> +{xpGained} XP
            </div>
          </div>
        </div>

        <div className="h-3 border-2 border-brand-ink rounded-full bg-white overflow-hidden mb-8">
          <div className="h-full bg-brand-mint border-r-2 border-brand-ink" style={{ width: `${(idx / cards.length) * 100}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="brutal-card p-10 md:p-12 bg-white min-h-[280px] flex flex-col items-center justify-center text-center"
          >
            <div className="text-sm font-bold uppercase text-brand-navy/50 tracking-wider mb-2">Front (target)</div>
            <div className="flex items-center gap-3">
              <div className="font-display text-4xl md:text-6xl font-bold text-brand-navy">{card.front}</div>
              <button
                type="button"
                onClick={() => pronounce(card)}
                data-testid="review-front-audio-btn"
                className="brutal-btn-yellow p-3 rounded-full shrink-0"
                title="Play pronunciation"
              >
                <Volume2 className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>
            {showBack && (
              <>
                <div className="w-32 h-0.5 bg-brand-ink/30 my-6" />
                <div className="text-sm font-bold uppercase text-brand-navy/50 tracking-wider mb-2">Back (English)</div>
                <div className="font-display text-3xl md:text-4xl font-bold text-brand-pink">{card.back}</div>
                {card.example && <div className="mt-3 text-brand-navy/70 italic">"{card.example}"</div>}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8">
          {!showBack ? (
            <button onClick={() => setShowBack(true)} data-testid="review-show-answer" className="brutal-btn-navy w-full text-lg py-4">
              Show answer
            </button>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button onClick={() => grade(1)} data-testid="review-grade-again" className="brutal-btn-pink py-4">
                <X className="w-4 h-4" strokeWidth={3} /> Again
              </button>
              <button onClick={() => grade(3)} data-testid="review-grade-hard" className="brutal-btn bg-brand-yellow py-4">
                Hard
              </button>
              <button onClick={() => grade(4)} data-testid="review-grade-good" className="brutal-btn bg-brand-mint py-4">
                Good
              </button>
              <button onClick={() => grade(5)} data-testid="review-grade-easy" className="brutal-btn-white py-4">
                <Check className="w-4 h-4" strokeWidth={3} /> Easy
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
